import api from './api'

/** Database Console — mirrors `backend/app/api/v1/db_console.py`. */

export type HealthCheck = { id: string; label: string; ok: boolean; detail: string }

/** Live / archived / environment breakdown of a table (or a search's matches).
 *  Present only for a table with an `archived_at` or `data_partition` column. */
export type Lifecycle = {
  /** `archived_at` when the table soft-deletes, else null. */
  archiveColumn: string | null
  /** `data_partition` when rows belong to an environment, else null. */
  environmentColumn: string | null
  liveRows: number
  archivedRows: number
  /** Of the archived rows, how many Settings -> Demo Data "reset test data"
   *  archived. Null for a table the reset never touches. */
  archivedByReset: number | null
  environments: Array<{ environment: string; liveRows: number; archivedRows: number }> | null
}

export type TableHealth = {
  table: string
  label: string
  present: boolean
  rows: number | null
  lifecycle?: Lifecycle | null
  lastWriteAt: string | null
  columns: number
  indexes: number
  rowAccess: boolean
}

export type DatabaseHealth = {
  checkedAt: string
  engine: {
    dialect: string
    version: string | null
    sizeBytes: number | null
    sizeNote: string | null
    activeConnections: number | null
    latencyMs: number | null
  }
  checks: HealthCheck[]
  healthy: boolean
  totals: { tables: number; tablesPresent: number; rows: number }
  tables: TableHealth[]
  largestTables: Array<{ table: string; approxRows: number; bytes: number }>
}

export type LedgerEntry = {
  key: string
  table: string
  label: string
  recordId: string | null
  occurredAt: string | null
  summary: Record<string, unknown>
  row: Record<string, unknown>
}

export type Ledger = {
  queriedAt: string
  since: string | null
  cursorApplied: string | null
  newestAt: string | null
  returned: number
  matched: number
  truncated: boolean
  /** The asking account's environment; a deal file opens only within it. */
  viewerEnvironment?: string
  sources: string[]
  entries: LedgerEntry[]
}

export type TableColumn = {
  name: string
  type: string
  nullable?: boolean
  primaryKey?: boolean
  redacted: boolean
}

export type TableInventory = {
  tables: Array<{
    table: string
    label: string
    bookmarked: boolean
    bookmarkRank: number | null
    present: boolean
    /** Every stored row, live and archived alike. */
    rows: number | null
    lifecycle?: Lifecycle | null
    columns: TableColumn[]
    rowAccess: boolean
    rowAccessNote: string | null
  }>
}

export type TableRows = {
  table: string
  label: string
  total: number
  /** Full table size before the search filter (omitted on older API responses). */
  tableTotal?: number
  page: number
  pageSize: number
  pageCount: number
  orderedBy: string
  q?: string | null
  rowKey?: Record<string, string> | null
  includeArchived?: boolean
  /** True when archived rows were left out of `rows` and `total`. */
  archivedHidden?: boolean
  /** How many archived rows that left out, within the current search. */
  archivedHiddenRows?: number | null
  /** The whole table, split. */
  lifecycle?: Lifecycle | null
  /** The rows the search matched, split (the whole table when not searching). */
  matchLifecycle?: Lifecycle | null
  viewerEnvironment?: string
  columns: TableColumn[]
  rows: Array<Record<string, unknown>>
}

export type ConsoleSearchHit = {
  /** 'field' — a catalogue field the plain-English query names, pointing at a
   *  deal cell that holds it. */
  kind: 'table' | 'column' | 'cell' | 'field'
  fieldKey?: string
  path?: string
  table: string
  label: string
  column: string | null
  rowKey: Record<string, string | number | boolean> | null
  displayValue: string | null
  bookmarked: boolean
  bookmarkRank: number | null
  /** The row the hit opens is archived. */
  archived?: boolean
  /** That row's environment (data_partition), when the table has one. */
  environment?: string | null
  /** Catalogue field hits: how many deals hold the field, live and archived apart. */
  holders?: {
    liveRows: number
    liveScanned: number
    archivedRows: number | null
    archivedScanned: number | null
    complete: boolean
  }
}

export type ConsoleSearchResponse = {
  q: string
  returned: number
  truncated: boolean
  skippedTables: string[]
  viewerEnvironment?: string
  results: ConsoleSearchHit[]
}

export type ExportRequest = {
  id: string
  status: 'pending' | 'approved' | 'denied' | 'completed' | 'cancelled'
  reason: string
  scope: string[]
  requestedByEmail: string
  requestedByName?: string | null
  /** The second approver the requester named; only they can decide it. Null on an older request. */
  designatedApproverEmail?: string | null
  designatedApproverName?: string | null
  includeAttachments?: boolean
  approvedByEmail: string | null
  approvedAt: string | null
  decisionNote: string | null
  tokenExpiresAt: string | null
  tokenExpired: boolean
  downloadedAt: string | null
  downloadedByEmail: string | null
  manifest: ExportManifest | null
  byteSize: number | null
  createdAt: string | null
  /** Present only on the approval response — shown once, never stored. */
  downloadToken?: string
}

export type ExportManifest = {
  requestId: string
  generatedAt: string
  requestedBy: string
  approvedBy: string
  downloadedBy: string
  complete: boolean
  note: string
  files: Array<{
    table: string
    file: string | null
    rowsInDatabase: number | null
    rowsWritten: number
    liveRows?: number | null
    archivedRows?: number | null
    sha256?: string
    bytes?: number
    complete?: boolean
    omitted: string | null
    redactedColumns?: string[]
  }>
}

export type ExportRequestList = {
  requests: ExportRequest[]
  exportableTables: string[]
  rowsPerTableCap: number
  tokenTtlMinutes: number
  viewerEmail: string
  /** What the viewer may do (absent on an older API: treat as allowed). */
  viewerCanRequest?: boolean
  viewerCanApprove?: boolean
  /** Pending requests raised by someone else that this viewer can decide. */
  awaitingViewer?: number
  /** What an export carries and what it does not, in the words shown wherever one is requested. */
  attachmentsNotice?: string
  attachmentLimitBytes?: number
}

export type ExportApprover = { email: string; name: string; role: string | null }

export const dbConsoleService = {
  health: () => api.get<DatabaseHealth>('/db-console/health').then((r) => r.data),

  ledger: (params: { since?: string | null; limit?: number; tables?: string | null }) =>
    api
      .get<Ledger>('/db-console/ledger', {
        params: {
          since: params.since ?? undefined,
          limit: params.limit ?? 100,
          tables: params.tables ?? undefined,
        },
      })
      .then((r) => r.data),

  tables: () => api.get<TableInventory>('/db-console/tables').then((r) => r.data),

  search: (q: string, signal?: AbortSignal) =>
    api
      .get<ConsoleSearchResponse>('/db-console/search', { params: { q }, signal })
      .then((r) => r.data),

  tableRows: (
    table: string,
    page: number,
    pageSize: number,
    q?: string,
    rowKey?: Record<string, string | number | boolean> | null,
    includeArchived?: boolean,
  ) =>
    api
      .get<TableRows>(`/db-console/tables/${encodeURIComponent(table)}/rows`, {
        params: {
          page,
          page_size: pageSize,
          ...(q?.trim() ? { q: q.trim() } : {}),
          ...(rowKey ? { row_key: JSON.stringify(rowKey) } : {}),
          ...(includeArchived ? { include_archived: true } : {}),
        },
      })
      .then((r) => r.data),

  listExportRequests: () =>
    api.get<ExportRequestList>('/db-console/export-requests').then((r) => r.data),

  listExportApprovers: () =>
    api
      .get<{ approvers: ExportApprover[] }>('/db-console/export-approvers')
      .then((r) => r.data),

  createExportRequest: (body: {
    reason: string
    tables: string[]
    approverEmail: string
    includeAttachments?: boolean
  }) => api.post<ExportRequest>('/db-console/export-requests', body).then((r) => r.data),

  approveExportRequest: (id: string, note: string) =>
    api
      .post<ExportRequest>(`/db-console/export-requests/${id}/approve`, { note })
      .then((r) => r.data),

  denyExportRequest: (id: string, note: string) =>
    api.post<ExportRequest>(`/db-console/export-requests/${id}/deny`, { note }).then((r) => r.data),

  cancelExportRequest: (id: string) =>
    api.post<ExportRequest>(`/db-console/export-requests/${id}/cancel`).then((r) => r.data),

  // Token goes in the body, never the query string — a capability token in a
  // URL ends up in access logs, Referer headers and browser history.
  downloadExport: async (id: string, token: string) => {
    const response = await api.post(
      `/db-console/export-requests/${id}/download`,
      { token },
      { responseType: 'blob' },
    )
    const disposition = response.headers['content-disposition'] as string | undefined
    const match = /filename="?([^"]+)"?/.exec(disposition ?? '')
    return {
      blob: response.data as Blob,
      filename: match?.[1] ?? 'database-export.zip',
      complete: String(response.headers['x-export-complete']) === 'true',
      rows: Number(response.headers['x-export-rows'] ?? 0),
      attachments: Number(response.headers['x-export-attachments'] ?? 0),
    }
  },
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}
